import { selectorFamily, useRecoilValue } from 'recoil';
import { isDateInRange } from 'functions';
import { DisputeStatus } from 'enums';
import { disputeFilter, disputeSummaries, useSetRefreshState } from 'state';
import { DisputeSummary } from 'types';
import Fuse from 'fuse.js';
import { getEffectiveAssigneeEmail, getEffectiveDisputeQueueId } from './disputeFilter';
import { userInfo } from './userInfo';

const matchesStatus = (disputeStatusId: DisputeStatus, status?: DisputeStatus) => {
    if (status === DisputeStatus.All) {
        return true;
    }

    if (status != null) {
        return disputeStatusId === status;
    }

    return disputeStatusId !== DisputeStatus.Closed;
};

const matchesAssignee = (assigneeEmail?: string, selectedAssigneeEmail?: string) => {
    if (!selectedAssigneeEmail || selectedAssigneeEmail === 'All') {
        return true;
    }

    if (selectedAssigneeEmail === 'Unassigned') {
        return !assigneeEmail;
    }

    return assigneeEmail?.toLowerCase() === selectedAssigneeEmail.toLowerCase();
};

export const filteredDisputes = selectorFamily<DisputeSummary[], string>({
    key: 'filteredDisputes',
    get: (accountId) =>
        async ({ get }) => {
            const filter = get(disputeFilter);
            const {
                dateRange,
                status,
                selectedAssignee,
                selectedQueue,
                selectedAdjusmentType,
                selectedMarDisputeTypeId,
                selectedReasonId,
                searchKeys,
                searchTerm,
                sort,
            } = filter;

            const disputes = get(disputeSummaries({ accountId }));
            const user = get(userInfo);
            const selectedQueueId = getEffectiveDisputeQueueId(selectedQueue, user);
            const effectiveAssigneeEmail = selectedAssignee
                ? getEffectiveAssigneeEmail(selectedAssignee, user)
                : undefined;

            const list = disputes.filter(({ disputeStatusId, submittedDateUtc, assigneeEmail, disputeQueueId, adjustmentTypeId, disputeReasonId, marDisputeTypeId }) => {
                return isDateInRange(dateRange, submittedDateUtc)
                && (selectedMarDisputeTypeId ? selectedMarDisputeTypeId === marDisputeTypeId : true)
                && (selectedReasonId ? selectedReasonId === disputeReasonId : true)
                && matchesStatus(disputeStatusId, status)
                && (selectedAdjusmentType ? adjustmentTypeId === selectedAdjusmentType : true)
                && matchesAssignee(assigneeEmail, effectiveAssigneeEmail)
                && (selectedQueueId ? disputeQueueId === selectedQueueId : true);
            });

            const isEscalatedAndNotCompleted = (dispute: DisputeSummary) => {
                return dispute.isEscalated && dispute.disputeStatusId !== DisputeStatus.Closed;
            }

            if (searchTerm) {
                const fuse = new Fuse(list, { keys: searchKeys as string[], threshold: 0.2, ignoreLocation: true });

                return fuse
                        .search(searchTerm)
                        .map((r) => r.item)
                        .sort(sort.compare)
            }

            return list.sort((a: DisputeSummary, b: DisputeSummary) => {
                if (isEscalatedAndNotCompleted(a) && !isEscalatedAndNotCompleted(b)) {
                    return -1;
                }

                if (!isEscalatedAndNotCompleted(a) && isEscalatedAndNotCompleted(b)) {
                    return 1;
                }

                return sort.compare(a, b);
            });
        }
});

export const useFilteredDisputes = (accountId: string) => useRecoilValue(filteredDisputes(accountId));

export const useRefreshFilteredDisputes = (accountId: string) => useSetRefreshState(disputeSummaries({ accountId }));
